import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  Paper,
  LinearProgress,
  Checkbox,
  Stack,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowForward, 
  School, 
  EmojiEvents, 
  Science,
  Checklist,
  Timeline,
  Psychology,
  Translate,
  EditOutlined,
  DeleteOutline,
  SaveOutlined,
  Close,
  ContentCopy,
} from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import DashboardAnnouncementDialog, { DashboardPost } from '../components/DashboardAnnouncementDialog';
import { formatChatMarkdown, hasLatexDelimiters } from '../components/ChatWidget/formatChatMarkdown';
import { countActivePaidFeatures, getPaidFeatureStatus } from '../utils/premiumAccess';
import { PREMIUM_FEATURES } from '../constants/premiumFeatures';

const examSections = [
  {
    title: 'WAEC',
    fullName: 'West African Examinations Council',
    description: 'Comprehensive preparation for WAEC examinations with detailed subject coverage and practice materials.',
    icon: <School sx={{ fontSize: 50, color: '#ffffff' }} />,
    color: '#FF6B6B',
    hoverColor: '#FF5252',
    slug: 'waec',
  },
  {
    title: 'JAMB',
    fullName: 'Joint Admissions and Matriculation Board',
    description: 'Master JAMB subjects with our curated study guides, practice questions, and expert explanations.',
    icon: <EmojiEvents sx={{ fontSize: 50, color: '#ffffff' }} />,
    color: '#4ECDC4',
    hoverColor: '#45B7D1',
    slug: 'jamb',
  },
  {
    title: 'Labs',
    fullName: 'Laboratory Practicals',
    description: 'Learn and simulate essential Chemistry, Physics, and Biology practicals with interactive demonstrations.',
    icon: <Science sx={{ fontSize: 50, color: '#ffffff' }} />,
    color: '#95E1D3',
    hoverColor: '#7FD8C4',
    slug: 'labs',
  },
  {
    title: 'Language Hub',
    fullName: 'English Language Learning Tools',
    description: 'Build vocabulary, grammar, writing, reading, and communication skills with focused interactive tools.',
    icon: <Translate sx={{ fontSize: 50, color: '#ffffff' }} />,
    color: '#8B9FE8',
    hoverColor: '#7186D8',
    slug: 'language-hub',
  },
];

interface RoadmapTask {
  id: string;
  text: string;
  completed: boolean;
}

interface DashboardSummary {
  mastery: Array<{ subject: string; subjectSlug: string; examType: 'WAEC' | 'JAMB'; score: number | null; attempts: number; continuePath: string }>;
  activity: Array<{ id: string; title: string; detail: string; score: number | null; at?: string; path: string }>;
  recommendations: Array<{ title: string; detail: string; path: string }>;
  announcements: Array<{ id: string; text: string }>;
  dashboardPost: DashboardPost | null;
  stats: { completedMockTests: number; averageScore: number | null };
  errors: { roadmap?: boolean; performance?: boolean; announcements?: boolean };
}

interface ReferralBenefitsSummary {
  rewards: Array<{ _id: string; discountPercent: number; milestone: number; expiresAt: string }>;
  program: { enabled: boolean; referralRewardPercent: number; milestones: number[] };
}

const defaultReferralBenefits: ReferralBenefitsSummary = {
  rewards: [],
  program: { enabled: true, referralRewardPercent: 5, milestones: [1, 3, 5] },
};

const emptyDashboard: DashboardSummary = {
  mastery: [],
  activity: [],
  recommendations: [],
  announcements: [],
  dashboardPost: null,
  stats: { completedMockTests: 0, averageScore: null },
  errors: {},
};

const HighSchoolHome: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const activePaidFeatureCount = countActivePaidFeatures(user?.paidFeatures, user?.paidFeatureExpiries);
  const hasAiTutorAccess = user?.role === 'admin' || activePaidFeatureCount >= 2;
  const userId = user?._id;
  const hasSchoolWorkAccess = Boolean(user?.school || user?.schoolName || user?.schoolEmail);
  const [jambCountdown, setJambCountdown] = useState('');
  const [waecCountdown, setWaecCountdown] = useState('');
  const [jambNextAttempt, setJambNextAttempt] = useState<Date | null>(null);
  const [waecNextAttempt, setWaecNextAttempt] = useState<Date | null>(null);
  const [jambCanAttempt, setJambCanAttempt] = useState(true);
  const [waecCanAttempt, setWaecCanAttempt] = useState(true);
  const [loadingMockTests, setLoadingMockTests] = useState(true);

  const [tasks, setTasks] = useState<RoadmapTask[]>([]);
  const [dashboard, setDashboard] = useState(emptyDashboard);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardRefresh, setDashboardRefresh] = useState(0);
  const [dashboardError, setDashboardError] = useState('');
  const [dashboardPostOpen, setDashboardPostOpen] = useState(false);
  const [referralBenefits, setReferralBenefits] = useState(defaultReferralBenefits);
  const [referralBenefitsLoading, setReferralBenefitsLoading] = useState(true);
  const [referralBenefitsError, setReferralBenefitsError] = useState('');
  const [referralCopied, setReferralCopied] = useState(false);
  const [referralCopyError, setReferralCopyError] = useState('');
  const [roadmapError, setRoadmapError] = useState('');
  const [newTask, setNewTask] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [editingTaskText, setEditingTaskText] = useState('');
  const [taskToDelete, setTaskToDelete] = useState<RoadmapTask | null>(null);
  const [deletingTask, setDeletingTask] = useState(false);

  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState('');
  const [aiAnswerHtml, setAiAnswerHtml] = useState('');
  const aiMathJaxLoading = useRef<Promise<void> | null>(null);

  useEffect(() => {
    if (!aiAnswerHtml || !hasLatexDelimiters(aiAnswer)) return;
    const typeset = async () => {
      if (!window.MathJax?.typesetPromise) {
        if (!aiMathJaxLoading.current) {
          window.MathJax = { tex: { inlineMath: [['$', '$'], ['\\(', '\\)']], displayMath: [['$$', '$$'], ['\\[', '\\]']] }, startup: { typeset: false } };
          aiMathJaxLoading.current = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.id = 'einstein-quick-query-mathjax';
            script.src = 'https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-mml-chtml.js';
            script.async = true;
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('MathJax failed to load'));
            document.head.appendChild(script);
          });
        }
        await aiMathJaxLoading.current;
      }
      await window.MathJax?.typesetPromise?.();
    };
    typeset().catch(() => undefined);
  }, [aiAnswer, aiAnswerHtml]);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    let active = true;
    if (!userId) {
      setTasks([]);
      setDashboard(emptyDashboard);
      setDashboardLoading(false);
      setDashboardPostOpen(false);
      return () => { active = false; };
    }

    setDashboardLoading(true);
    setDashboardError('');
    setTasks([]);
    setDashboard(emptyDashboard);
    api.get('/student-dashboard')
      .then((response) => {
        if (!active) return;
        const data = response.data?.data;
        const dashboardPost: DashboardPost | null = data?.dashboardPost || null;
        setTasks(Array.isArray(data?.roadmap) ? data.roadmap : []);
        setDashboard({
          ...emptyDashboard,
          ...data,
          mastery: Array.isArray(data?.mastery) ? data.mastery : [],
          activity: Array.isArray(data?.activity) ? data.activity : [],
          recommendations: Array.isArray(data?.recommendations) ? data.recommendations : [],
          announcements: Array.isArray(data?.announcements) ? data.announcements : [],
          dashboardPost,
          stats: { ...emptyDashboard.stats, ...data?.stats },
        });
        setDashboardPostOpen(Boolean(dashboardPost));
      })
      .catch(() => {
        if (active) setDashboardError('Unable to load your learning overview.');
      })
      .finally(() => {
        if (active) setDashboardLoading(false);
      });

    return () => { active = false; };
  }, [userId, dashboardRefresh]);

  useEffect(() => {
    let active = true;
    if (!userId) {
      setReferralBenefits(defaultReferralBenefits);
      setReferralBenefitsLoading(false);
      setReferralBenefitsError('');
      return () => { active = false; };
    }

    setReferralBenefitsLoading(true);
    setReferralBenefitsError('');
    api.get('/users/referral-benefits')
      .then((response) => {
        if (!active) return;
        const data = response.data?.data;
        const program = data?.program || {};
        setReferralBenefits({
          rewards: Array.isArray(data?.rewards) ? data.rewards : [],
          program: {
            enabled: program.enabled !== false,
            referralRewardPercent: Number(program.referralRewardPercent ?? 5),
            milestones: Array.isArray(program.milestones) && program.milestones.length
              ? program.milestones
              : [1, 3, 5],
          },
        });
      })
      .catch(() => {
        if (active) setReferralBenefitsError('Unable to load your current referral rewards.');
      })
      .finally(() => {
        if (active) setReferralBenefitsLoading(false);
      });

    return () => { active = false; };
  }, [userId]);

  const handleDismissDashboardPost = () => {
    setDashboardPostOpen(false);
  };

  const handleToggleTask = async (task: RoadmapTask) => {
    const completed = !task.completed;
    setRoadmapError('');
    setTasks((previous) => previous.map((item) => item.id === task.id ? { ...item, completed } : item));
    try {
      await api.patch(`/student-dashboard/roadmap/${task.id}`, { completed });
    } catch {
      setTasks((previous) => previous.map((item) => item.id === task.id ? { ...item, completed: task.completed } : item));
      setRoadmapError('Unable to update that roadmap task. Try again.');
    }
  };

  const handleAddTask = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const text = newTask.trim();
    if (!text || savingTask) return;
    setSavingTask(true);
    setRoadmapError('');
    try {
      const response = await api.post('/student-dashboard/roadmap', { text });
      setTasks((previous) => [response.data.data, ...previous]);
      setNewTask('');
    } catch (error: any) {
      setRoadmapError(error.response?.data?.message || 'Unable to save that roadmap task. Try again.');
    } finally {
      setSavingTask(false);
    }
  };

  const handleSaveTask = async (task: RoadmapTask) => {
    const text = editingTaskText.trim();
    if (!text || text.length > 160) return;
    setSavingTask(true);
    setRoadmapError('');
    try {
      const response = await api.patch(`/student-dashboard/roadmap/${task.id}`, { text });
      const updatedTask = response.data?.data;
      setTasks((previous) => previous.map((item) => item.id === task.id ? { ...item, ...updatedTask, text } : item));
      setEditingTaskId(null);
      setEditingTaskText('');
    } catch {
      setRoadmapError('Unable to save that roadmap task. Try again.');
    } finally {
      setSavingTask(false);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete || deletingTask) return;
    setDeletingTask(true);
    setRoadmapError('');
    try {
      await api.delete(`/student-dashboard/roadmap/${taskToDelete.id}`);
      setTasks((previous) => previous.filter((task) => task.id !== taskToDelete.id));
      setTaskToDelete(null);
    } catch {
      setRoadmapError('Unable to delete that roadmap task. Try again.');
    } finally {
      setDeletingTask(false);
    }
  };

  const handleAskEinstein = async () => {
    if (!hasAiTutorAccess) return;
    if (!aiQuestion.trim()) return;
    setAiLoading(true);
    setAiAnswer('');
    setAiAnswerHtml('');
    try {
      const response = await api.post('/chat', { message: aiQuestion });
      const answer = response.data?.reply || response.data?.response || 'Einstein is ready to assist you!';
      setAiAnswer(answer);
      setAiAnswerHtml(formatChatMarkdown(String(answer)));
    } catch (e) {
      setAiAnswer('Einstein: I encountered an error connecting to my servers. Please try again!');
    } finally {
      setAiLoading(false);
    }
  };

  // Canonicalize URL: if this page is rendered at /high-school, replace URL to root '/'
  useEffect(() => {
    if (location.pathname === '/high-school') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  // Fetch mock test attempt info
  useEffect(() => {
    if (!user) {
      setLoadingMockTests(false);
      return;
    }

    const fetchAttemptInfo = async () => {
      try {
        const [jambRes, waecRes] = await Promise.all([
          api.get('/mock-test/info/last-attempt', { params: { examType: 'JAMB' } }),
          api.get('/mock-test/info/last-attempt', { params: { examType: 'WAEC' } }),
        ]);

        setJambCanAttempt(jambRes.data.canAttempt);
        setWaecCanAttempt(waecRes.data.canAttempt);

        if (!jambRes.data.canAttempt && jambRes.data.nextAttemptDate) {
          setJambNextAttempt(new Date(jambRes.data.nextAttemptDate));
        }
        if (!waecRes.data.canAttempt && waecRes.data.nextAttemptDate) {
          setWaecNextAttempt(new Date(waecRes.data.nextAttemptDate));
        }
        setLoadingMockTests(false);
      } catch (error) {
        console.error('Error fetching mock test info:', error);
        setLoadingMockTests(false);
      }
    };

    fetchAttemptInfo();
  }, [user]);

  // JAMB countdown timer
  useEffect(() => {
    if (!jambNextAttempt) {
      setJambCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = jambNextAttempt.getTime() - now.getTime();

      if (diff <= 0) {
        setJambCountdown('');
        setJambCanAttempt(true);
        setJambNextAttempt(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setJambCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [jambNextAttempt]);

  useEffect(() => {
    if (!waecNextAttempt) {
      setWaecCountdown('');
      return;
    }

    const updateCountdown = () => {
      const diff = waecNextAttempt.getTime() - Date.now();
      if (diff <= 0) {
        setWaecCountdown('');
        setWaecCanAttempt(true);
        setWaecNextAttempt(null);
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      const seconds = Math.floor((diff / 1000) % 60);
      setWaecCountdown(`${days}d ${hours}h ${minutes}m ${seconds}s`);
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [waecNextAttempt]);

  const currentHour = new Date().getHours();
  const greeting = currentHour < 12 ? 'Good morning' : currentHour < 18 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'Student';
  const totalTasks = tasks.length;
  const completedTasks = tasks.filter((task) => task.completed).length;
  const roadmapProgress = totalTasks ? (completedTasks / totalTasks) * 100 : 0;
  const continueTask = tasks.find((task) => !task.completed) || tasks[0] || null;

  const quickActions = [
    { label: 'WAEC', path: '/waec', accent: 'primary' },
    { label: 'JAMB', path: '/jamb', accent: 'secondary' },
    { label: 'Practice', path: hasSchoolWorkAccess ? '/school-work/materials' : '/labs', accent: 'success' },
    { label: 'Mock Tests', path: '/mock-tests', accent: 'warning' },
    { label: 'AI Tutor', path: '/chat', accent: 'info' },
  ];

  const membershipPreviewKeys = ['waecMockTest', 'jambMockTest', 'labs'];
  const membershipStatus = PREMIUM_FEATURES
    .filter((feature) => membershipPreviewKeys.includes(feature.key))
    .map((feature) => ({ ...feature, ...getPaidFeatureStatus(user, feature.key) }));
  const continueLearningPath = dashboard.mastery[0]?.continuePath || '/waec';
  const referralCredits = PREMIUM_FEATURES.filter((feature) => Number(user?.referralDiscountCredits?.[feature.key]) > 0);

  const copyReferralCode = async () => {
    if (!user?.referralCode) return;
    try {
      await navigator.clipboard.writeText(user.referralCode);
      setReferralCopied(true);
      setReferralCopyError('');
      window.setTimeout(() => setReferralCopied(false), 1800);
    } catch {
      setReferralCopied(false);
      setReferralCopyError('Could not copy the referral code. Please copy it manually.');
    }
  };

  const recentActivity = dashboard.activity;
  const announcements = dashboard.announcements;
  const masteryMetrics = dashboard.mastery;

  return (
    <Box sx={{ background: theme.palette.background.default, minHeight: '100vh', color: 'var(--ink)' }}>
      <DashboardAnnouncementDialog
        open={dashboardPostOpen}
        post={dashboard.dashboardPost}
        onClose={handleDismissDashboardPost}
        onNavigate={navigate}
      />
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
        <Box sx={{ mb: { xs: 3, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="overline" sx={{ display: 'block', letterSpacing: '0.12em', color: 'text.secondary', mb: 1 }}>
              A3Tech Education
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: '-0.04em', mb: 0.5 }}>
              {greeting}, {firstName} 👋
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 620 }}>
              Ready to continue your WAEC and JAMB preparation? Choose your next step and keep your momentum going.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Button variant="contained" onClick={() => navigate(continueLearningPath)} sx={{ borderRadius: 2 }}>
              Continue Learning
            </Button>
            <Button variant="outlined" onClick={() => navigate('/mock-tests')} sx={{ borderRadius: 2 }}>
              Take a Mock Test
            </Button>
          </Box>
        </Box>
        <Paper
          component="nav"
          aria-label="Dashboard quick navigation"
          elevation={0}
          sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
        >
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Find what you need
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Use these shortcuts to jump to learning paths, practice, your roadmap, or study resources.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button component="a" href="#dashboard-learning-path" size="small" variant="outlined">Learning paths</Button>
            <Button component="a" href="#dashboard-mock-tests" size="small" variant="outlined">Mock tests</Button>
            <Button component="a" href="#dashboard-roadmap" size="small" variant="outlined">Weekly roadmap</Button>
            <Button component="a" href="#dashboard-resources" size="small" variant="outlined">Study resources</Button>
          </Box>
        </Paper>
        {dashboardError && (
          <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => setDashboardRefresh((refresh) => refresh + 1)}>Retry</Button>}>
            {dashboardError}
          </Alert>
        )}

        <Paper component="section" elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
            <Box>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>Referral Code</Typography>
              <Typography variant="body2" color="text.secondary">
                {referralBenefitsLoading
                  ? 'Loading the current referral reward details…'
                  : referralBenefitsError
                    ? 'Unable to load the current referral reward details.'
                    : referralBenefits.program.enabled
                      ? `Share your code. Earn ${referralBenefits.program.referralRewardPercent}% referral rewards when referred students pay for ${referralBenefits.program.milestones.map((milestone) => `${milestone}${milestone === 1 ? 'st' : milestone === 2 ? 'nd' : milestone === 3 ? 'rd' : 'th'}`).join(', ')} distinct premium features.`
                      : 'Referral rewards are temporarily paused.'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {typeof user?.referralCount === 'number'
                  ? `Used by ${user.referralCount} ${user.referralCount === 1 ? 'person' : 'people'}.`
                  : 'Referral signup activity is loading.'}
              </Typography>
            </Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography component="code" variant="h6" sx={{ px: 1.5, py: 0.75, bgcolor: 'action.hover', borderRadius: 1, letterSpacing: '0.08em' }}>
                {user?.referralCode || 'Loading code…'}
              </Typography>
              <Tooltip title={referralCopied ? 'Copied' : 'Copy referral code'}>
                <span>
                  <IconButton aria-label="Copy referral code" onClick={copyReferralCode} disabled={!user?.referralCode}>
                    <ContentCopy />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Available referral rewards: {referralBenefitsLoading
              ? 'loading…'
              : referralBenefitsError
                ? 'unable to confirm right now'
                : !referralBenefits.program.enabled
                  ? 'referral program paused'
                  : referralBenefits.rewards.length
                    ? referralBenefits.rewards.map((reward) => `${reward.discountPercent}% (milestone ${reward.milestone}, expires ${new Date(reward.expiresAt).toLocaleDateString()})`).join('; ')
                    : 'none right now'}
          </Typography>
          {referralCredits.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Legacy feature-specific discounts: {referralCredits.map((feature) => `${feature.label} (${user?.referralDiscountCredits?.[feature.key]})`).join(', ')}
            </Typography>
          )}
          {referralBenefitsError && <Alert severity="error" sx={{ mt: 1 }}>{referralBenefitsError}</Alert>}
          {referralCopyError && <Alert severity="error" sx={{ mt: 1 }}>{referralCopyError}</Alert>}
        </Paper>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                background: 'linear-gradient(135deg, rgba(30,58,138,0.08), rgba(37,99,235,0.04))',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Continue Learning
                </Typography>
                <Typography variant="caption" color="text.secondary" aria-live="polite">
                  {dashboardLoading ? 'Loading your study plan...' : `${completedTasks} of ${totalTasks} roadmap tasks completed`}
                </Typography>
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                {continueTask ? continueTask.text : 'You haven’t started learning yet.'}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <LinearProgress variant={dashboardLoading ? 'indeterminate' : 'determinate'} value={roadmapProgress} sx={{ height: 8, borderRadius: 999 }} />
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {continueTask ? 'Your next roadmap task' : 'Explore WAEC and JAMB subjects to get started.'}
                </Typography>
                <Button size="small" variant="text" onClick={() => navigate('/waec')} endIcon={<ArrowForward />}>
                  Explore WAEC
                </Button>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                What would you like to do?
              </Typography>
              <Grid container spacing={1.5}>
                {quickActions.map((action) => (
                  <Grid item xs={6} key={action.label}>
                    <Button
                      fullWidth
                      variant={action.accent === 'primary' ? 'contained' : 'outlined'}
                      onClick={() => navigate(action.path)}
                      sx={{
                        minHeight: 44,
                        borderRadius: 2,
                        fontWeight: 600,
                        textTransform: 'none',
                        justifyContent: 'center',
                        ...(action.accent === 'secondary' && { borderColor: 'var(--primary)', color: 'var(--primary)' }),
                        ...(action.accent === 'success' && { borderColor: 'var(--success)', color: 'var(--success)' }),
                        ...(action.accent === 'warning' && { borderColor: '#d97706', color: '#d97706' }),
                        ...(action.accent === 'info' && { borderColor: '#0ea5e9', color: '#0ea5e9' }),
                      }}
                    >
                      {action.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Progress overview</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Roadmap tasks</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{dashboardLoading ? 'Loading' : `${completedTasks} of ${totalTasks}`}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Completed mock tests</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{dashboardLoading ? 'Loading' : dashboard.stats.completedMockTests}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">Recent average mock score</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{dashboardLoading ? 'Loading' : dashboard.stats.averageScore === null ? 'No results yet' : `${dashboard.stats.averageScore}%`}</Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Recommended for you</Typography>
              <Stack spacing={1.5}>
                <Typography variant="caption" color="text.secondary">Based on your last five WAEC/JAMB subject visits; completed mock-test averages below 70% are prioritized.</Typography>
                {dashboardLoading ? <Typography variant="body2" color="text.secondary">Loading recommendations...</Typography> : dashboard.errors.performance ? <Typography variant="body2" color="error">Unable to load recommendations.</Typography> : dashboard.recommendations.length ? dashboard.recommendations.map((recommendation) => (
                  <Box key={recommendation.title} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(148, 163, 184, 0.06)' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{recommendation.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{recommendation.detail}</Typography>
                    </Box>
                    <Button size="small" onClick={() => navigate(recommendation.path)} endIcon={<ArrowForward />}>
                      Review
                    </Button>
                  </Box>
                )) : <Typography variant="body2" color="text.secondary">Take a mock test to get study recommendations based on your results.</Typography>}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Paper id="dashboard-roadmap" elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checklist color="primary" /> Weekly Roadmap
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">{dashboardLoading ? 'Loading your roadmap...' : `${completedTasks} of ${totalTasks} completed`}</Typography>
                <LinearProgress variant={dashboardLoading ? 'indeterminate' : 'determinate'} value={roadmapProgress} sx={{ mt: 1, height: 8, borderRadius: 999 }} />
              </Box>
              {roadmapError && <Alert severity="error" sx={{ mb: 1 }}>{roadmapError}</Alert>}
              <Box component="form" onSubmit={handleAddTask} sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="Add a study task"
                  helperText="Add a specific action you plan to complete, e.g. “Review WAEC Physics: Forces”. This is your personal checklist."
                  value={newTask}
                  onChange={(event) => setNewTask(event.target.value)}
                  inputProps={{ maxLength: 160 }}
                  disabled={tasks.length >= 20 || savingTask}
                />
                <Button type="submit" variant="outlined" disabled={!newTask.trim() || tasks.length >= 20 || savingTask} sx={{ minWidth: 92, minHeight: 40 }}>
                  {savingTask ? 'Saving' : 'Add task'}
                </Button>
              </Box>
              <Stack spacing={1.5}>
                {dashboardLoading ? <Typography variant="body2" color="text.secondary">Loading your roadmap...</Typography> : dashboard.errors.roadmap ? <Typography variant="body2" color="error">Unable to load your roadmap.</Typography> : tasks.length ? tasks.map((task) => (
                  <Box key={task.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    {editingTaskId === task.id ? (
                      <>
                        <TextField
                          size="small"
                          fullWidth
                          label="Edit study task"
                          value={editingTaskText}
                          onChange={(event) => setEditingTaskText(event.target.value)}
                          inputProps={{ maxLength: 160 }}
                          autoFocus
                        />
                        <Tooltip title="Save task">
                          <span><IconButton aria-label={`Save task: ${task.text}`} onClick={() => handleSaveTask(task)} disabled={!editingTaskText.trim() || savingTask}><SaveOutlined /></IconButton></span>
                        </Tooltip>
                        <Tooltip title="Cancel editing">
                          <IconButton aria-label="Cancel task edit" onClick={() => { setEditingTaskId(null); setEditingTaskText(''); }}><Close /></IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Checkbox checked={task.completed} onChange={() => handleToggleTask(task)} size="small" inputProps={{ 'aria-label': `${task.completed ? 'Mark incomplete' : 'Complete'}: ${task.text}` }} />
                        <Typography variant="body2" sx={{ flexGrow: 1, alignSelf: 'center', textDecoration: task.completed ? 'line-through' : 'none', color: task.completed ? 'text.disabled' : 'text.primary' }}>
                          {task.text}
                        </Typography>
                        <Tooltip title="Edit task">
                          <IconButton aria-label={`Edit task: ${task.text}`} onClick={() => { setEditingTaskId(task.id); setEditingTaskText(task.text); }} size="small"><EditOutlined fontSize="small" /></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete task">
                          <IconButton aria-label={`Delete task: ${task.text}`} onClick={() => setTaskToDelete(task)} size="small"><DeleteOutline fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                )) : (
                  <Typography variant="body2" color="text.secondary">Your learning roadmap will appear here as you begin studying.</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Timeline color="secondary" /> Subject Mastery
              </Typography>
              <Stack spacing={2}>
                {dashboardLoading ? <Typography variant="body2" color="text.secondary">Loading subject results...</Typography> : dashboard.errors.performance ? <Typography variant="body2" color="error">Unable to load subject performance.</Typography> : masteryMetrics.length ? masteryMetrics.map((topic) => (
                    <Box key={`${topic.examType}-${topic.subject}`}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{topic.subject} ({topic.examType})</Typography>
                      <Typography variant="body2" color={topic.examType === 'WAEC' ? 'primary' : 'secondary'} sx={{ fontWeight: 700 }}>{topic.score === null ? 'Visited' : `${topic.score}%`}</Typography>
                    </Box>
                    {topic.score !== null && <LinearProgress variant="determinate" value={topic.score} color={topic.examType === 'WAEC' ? 'primary' : 'secondary'} sx={{ height: 7, borderRadius: 999 }} aria-label={`${topic.subject} ${topic.examType} mastery`} />}
                    <Typography variant="caption" color="text.secondary">{topic.attempts ? `Based on ${topic.attempts} recent mock test${topic.attempts === 1 ? '' : 's'}` : 'No mock-test results for this subject yet.'}</Typography>
                  </Box>
                )) : <Typography variant="body2" color="text.secondary">Visit WAEC or JAMB subjects to build your recent-subject list.</Typography>}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Psychology color="success" /> Ask A3Tech AI Tutor
              </Typography>

              {!hasAiTutorAccess && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Subscribe to at least two paid features to use the AI Tutor. You currently have {activePaidFeatureCount} active {activePaidFeatureCount === 1 ? 'feature' : 'features'}.
                  <Button size="small" onClick={() => navigate('/payment')} sx={{ ml: 1 }}>Choose features</Button>
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <input
                  type="text"
                  aria-label="Ask A3Tech AI Tutor a question"
                  placeholder="Ask a question..."
                  value={aiQuestion}
                  disabled={!hasAiTutorAccess}
                  onChange={(e) => setAiQuestion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAskEinstein(); }}
                  style={{
                    flexGrow: 1,
                    minHeight: 44,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: '1px solid #cbd5e1',
                    background: 'transparent',
                    color: 'inherit',
                    fontSize: '0.95rem',
                  }}
                />
                <Button variant="contained" onClick={handleAskEinstein} disabled={aiLoading || !hasAiTutorAccess} sx={{ minWidth: 72 }}>
                  {aiLoading ? '...' : 'Ask'}
                </Button>
              </Box>

              {(aiAnswer || aiLoading) && (
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(148,163,184,0.06)', border: '1px solid', borderColor: 'divider', minHeight: 90, overflow: 'auto' }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'success.main', fontWeight: 700, mb: 0.75 }}>
                    AI Tutor reply
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                    {aiLoading ? 'Thinking...' : <Box dangerouslySetInnerHTML={{ __html: aiAnswerHtml }} />}
                  </Typography>
                </Box>
              )}

              <Button variant="text" onClick={() => navigate('/chat')} disabled={!hasAiTutorAccess} endIcon={<ArrowForward />} sx={{ alignSelf: 'flex-start', mt: 2, p: 0, fontWeight: 700 }}>
                Open Full AI Tutor
              </Button>
            </Paper>
          </Grid>
        </Grid>

        <Paper id="dashboard-learning-path" elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Choose Your Learning Path
          </Typography>
          <Grid container spacing={2.5}>
            {examSections.map((section) => (
              <Grid item xs={12} md={6} key={section.slug}>
                <Box
                  sx={{
                    borderRadius: 3,
                    p: 2.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: section.slug === 'waec' ? 'linear-gradient(135deg, rgba(178, 67, 65, 0.08), rgba(178, 67, 65, 0.02))' : section.slug === 'jamb' ? 'linear-gradient(135deg, rgba(15, 107, 107, 0.08), rgba(15, 107, 107, 0.02))' : section.slug === 'labs' ? 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.02))' : 'linear-gradient(135deg, rgba(99,102,241,0.08), rgba(99,102,241,0.02))',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    {section.icon}
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{section.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {section.description}
                  </Typography>
                  <Button size="small" variant="text" onClick={() => navigate(`/${section.slug}`)} endIcon={<ArrowForward />} sx={{ p: 0, fontWeight: 700 }}>
                    Open path
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={7}>
            <Paper id="dashboard-mock-tests" elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Mock Tests</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', background: 'rgba(15, 107, 107, 0.05)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>JAMB</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Mathematics and core subject practice</Typography>
                    <Button variant="contained" fullWidth onClick={() => navigate('/mock-test/jamb')} disabled={!jambCanAttempt || loadingMockTests}>
                      {jambCanAttempt ? 'Start Mock Test' : 'Next attempt'}
                    </Button>
                    {jambCountdown && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Next attempt: {jambCountdown}</Typography>}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', background: 'rgba(178, 67, 65, 0.05)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>WAEC</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Exam-style readiness checks and revision</Typography>
                    <Button variant="contained" fullWidth onClick={() => navigate('/mock-test/waec')} disabled={!waecCanAttempt || loadingMockTests}>
                      {waecCanAttempt ? 'Start Mock Test' : 'Next attempt'}
                    </Button>
                    {waecCountdown && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Next attempt: {waecCountdown}</Typography>}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Membership & Access</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Check feature access and expiry dates here.</Typography>
              <Stack spacing={2}>
                {membershipStatus.map((status) => (
                  <Box key={status.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'rgba(148,163,184,0.04)' }}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{status.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{status.expiryLabel}</Typography>
                    </Box>
                    <Button
                      size="small"
                      variant={status.active ? 'outlined' : 'contained'}
                      onClick={() => status.active
                        ? navigate(status.path)
                        : navigate('/payment', { state: { featureKey: status.key, featureLabel: status.label, selectedFeatures: [status.key] } })}
                    >
                      {status.active ? 'Open' : status.expired ? 'Renew' : 'Get access'}
                    </Button>
                  </Box>
                ))}
              </Stack>
              <Button onClick={() => navigate('/membership')} sx={{ alignSelf: 'flex-start', mt: 1 }}>
                View All
              </Button>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Recent Activity</Typography>
              <Stack spacing={1.5}>
                {dashboardLoading ? <Typography variant="body2" color="text.secondary">Loading recent activity...</Typography> : dashboard.errors.performance ? <Typography variant="body2" color="error">Unable to load recent activity.</Typography> : recentActivity.length ? recentActivity.map((activity) => (
                  <Box key={activity.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Box sx={{ mt: 0.25, width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.primary">{activity.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{activity.detail}{activity.score !== null ? ` · ${activity.score}%` : ''}</Typography>
                    </Box>
                  </Box>
                )) : (
                  <Typography variant="body2" color="text.secondary">No recent activity yet. Start learning and your activity will appear here.</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Announcements</Typography>
              <Stack spacing={1.5}>
                {dashboardLoading ? <Typography variant="body2" color="text.secondary">Loading announcements...</Typography> : dashboard.errors.announcements ? <Typography variant="body2" color="error">Unable to load announcements.</Typography> : announcements.length ? announcements.map((item) => (
                  <Box key={item.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(37,99,235,0.05)' }}>
                    <Typography variant="body2">{item.text}</Typography>
                  </Box>
                )) : (
                  <Typography variant="body2" color="text.secondary">No new announcements right now.</Typography>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        <Paper id="dashboard-resources" elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Study Resources</Typography>
          <Grid container spacing={2}>
            {[
              { title: 'WAEC Revision', description: 'Topics, notes, and guidance for your exam preparation.', path: '/waec' },
              { title: 'JAMB Practice', description: 'Ready-to-use subject resources and revision points.', path: '/jamb' },
              { title: 'Labs & Language', description: 'Practical and language learning support for broader study.', path: '/labs' },
            ].map((resource) => (
              <Grid item xs={12} md={4} key={resource.title}>
                <Box sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>{resource.title}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{resource.description}</Typography>
                  <Button variant="outlined" onClick={() => navigate(resource.path)} sx={{ borderRadius: 2 }}>
                    Open
                  </Button>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Container>
      <Dialog open={Boolean(taskToDelete)} onClose={() => !deletingTask && setTaskToDelete(null)}>
        <DialogTitle>Delete study task?</DialogTitle>
        <DialogContent>
          <DialogContentText>This will permanently remove “{taskToDelete?.text}” from your Weekly Roadmap.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTaskToDelete(null)} disabled={deletingTask}>Cancel</Button>
          <Button onClick={handleDeleteTask} color="error" disabled={deletingTask} autoFocus>{deletingTask ? 'Deleting...' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HighSchoolHome;
